import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import React, {useCallback, useState, useMemo, useRef, useEffect} from 'react';

import CloseButton from './CloseButton';
import SegmentList from './context/SegmentsListDto';
import {useSegmentContext} from './context/context';
import {ISegmentDto} from './context/SegmentDtoInterface';

import './SegmentControl.scss';

interface SegmentControlProps {
  className?: string;
  segment: ISegmentDto;
  dragContainerRef?: React.RefObject<HTMLDivElement>;

  onBlur?: () => void;
  onHover?: () => void;
  onRemove?: () => void;
  getSegmentLegend?: (segment: ISegmentDto) => string;
  onDrag?: (newStart: number, newEnd: number) => Promise<boolean>;
  onLeftClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onRightClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const SegmentControl: React.FC<SegmentControlProps> = ({
  segment,
  className = '',
  dragContainerRef,

  onBlur,
  onHover,
  onDrag,
  onRemove,
  onLeftClick,
  onRightClick,
  getSegmentLegend,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [optimisticSegment, setOptimisticSegment] = useState(segment);
  const lastDragPromiseRef = useRef<{cancel: () => void} | null>(null);

  const {activeSegmentId} = useSegmentContext();
  const isActiveSegment = useMemo(() => activeSegmentId === segment.id, [
    activeSegmentId,
    segment,
  ]);

  useEffect(() => {
    if (JSON.stringify(segment) !== JSON.stringify(optimisticSegment)) {
      setOptimisticSegment(segment);
    }
  }, [optimisticSegment, segment]);

  const handleMouseEnter = useCallback(() => {
    onHover && onHover();
  }, [onHover]);

  const handleMouseLeave = useCallback(() => {
    onBlur && onBlur();
  }, [onBlur]);

  const handleDragStart = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, isLeft: boolean) => {
      event.preventDefault();
      setIsDragging(true);
      setIsDraggingLeft(isLeft);
    },
    []
  );

  const handleDrag = useCallback(
    async (event: MouseEvent, isLeft: boolean) => {
      if (isDragging && onDrag && dragContainerRef?.current) {
        const currentMousePosition = event.clientX;
        const containerRect = dragContainerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerLeft = containerRect.left;

        const absolutePositionPercentage = Math.max(
          0,
          Math.min(100, ((currentMousePosition - containerLeft) / containerWidth) * 100)
        );
        const roundedPosition = Math.round(absolutePositionPercentage);

        let newStart = optimisticSegment.start;
        let newEnd = optimisticSegment.end;

        if (isLeft) {
          newStart = Math.max(
            0,
            Math.min(roundedPosition, optimisticSegment.end - SegmentList.INCREMENTS)
          );
        } else {
          newEnd = Math.min(100, Math.max(optimisticSegment.start + 1, roundedPosition));
        }

        if (
          !isNaN(newStart) &&
          !isNaN(newEnd) &&
          newStart < newEnd &&
          (newStart !== optimisticSegment.start || newEnd !== optimisticSegment.end)
        ) {
          setOptimisticSegment(prev => ({...prev, start: newStart, end: newEnd}));

          if (lastDragPromiseRef.current) {
            lastDragPromiseRef.current.cancel();
          }

          let isCancelled = false;
          const newPromise = {
            cancel: () => {
              isCancelled = true;
            },
          };
          lastDragPromiseRef.current = newPromise;

          const missionAccomplished = await onDrag(newStart, newEnd);

          if (!isCancelled && !missionAccomplished) {
            setOptimisticSegment(segment);
          }
        }
      }
    },
    [isDragging, optimisticSegment, onDrag, dragContainerRef, segment]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);

    if (lastDragPromiseRef.current) {
      lastDragPromiseRef.current.cancel();
      lastDragPromiseRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (isDragging) {
        handleDrag(event, isDraggingLeft);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        handleDragEnd();
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleDrag, handleDragEnd, isDraggingLeft]);

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`segment-control ${className}`}
      style={{
        left: `${optimisticSegment.start}%`,
        width: `${optimisticSegment.end - optimisticSegment.start}%`,
      }}
    >
      {isActiveSegment && optimisticSegment.end - optimisticSegment.start > 0 ? (
        <CloseButton onRemove={onRemove!} className="remove-button" />
      ) : null}

      <div className={`segment ${isActiveSegment ? 'active-segment' : ''}`}>
        <button
          className="handler-button handler-button--left"
          onClick={onLeftClick}
          onMouseDown={event => handleDragStart(event, true)}
        >
          <ChevronLeft fontSize="small" className="handler" />
        </button>

        <div className="space-between-handlers">
          <div className="guideline" />
        </div>

        <button
          className="handler-button handler-button--right"
          onClick={onRightClick}
          onMouseDown={event => handleDragStart(event, false)}
        >
          <ChevronRight fontSize="small" className="handler" />
        </button>
      </div>

      {getSegmentLegend && optimisticSegment.length > 1 && isActiveSegment ? (
        <div className="timeContainer">
          <span>{getSegmentLegend(optimisticSegment)}</span>
        </div>
      ) : null}
    </div>
  );
};

export default SegmentControl;
